import React, { useState } from "react";
import { submitInquiry } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import PhoneInput from "./PhoneInput";
import { useI18n } from "../lib/i18n";
import { useSettings } from "../lib/settings";
import { useToast } from "../hooks/use-toast";
import { Loader2, MapPin, Mail, Phone, CheckCircle2 } from "lucide-react";
import { T } from "../lib/autoTranslate";

const ContactForm = ({ compact = false }) => {
  const { t, lang } = useI18n();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone_country_code: "+1",
    phone: "",
    subject: "",
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitInquiry({
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        phone_country_code: form.phone ? form.phone_country_code : null,
        subject: form.subject || null,
        message: form.message,
        language: lang,
      });
      setDone(true);
      toast({ title: "Message received", description: "Check your email for a confirmation. We reply within a day." });
      setForm({ full_name: "", email: "", phone_country_code: "+1", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done && compact) {
    return (
      <div className="py-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
          <CheckCircle2 className="text-green-600" size={28} />
        </div>
        <p className="text-[15px]">Thanks. We'll reply within a day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="c-name" className="text-[13px]">{t("full_name")}</Label>
          <Input id="c-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="c-email" className="text-[13px]">{t("email")}</Label>
          <Input id="c-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1" />
        </div>
      </div>
      <PhoneInput
        id="c-phone"
        label={`${t("phone")} · WhatsApp`}
        code={form.phone_country_code}
        number={form.phone}
        onCodeChange={(v) => update("phone_country_code", v)}
        onNumberChange={(v) => update("phone", v)}
      />
      <div>
        <Label htmlFor="c-subject" className="text-[13px]">{t("subject")}</Label>
        <Input id="c-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="mt-1" placeholder="Booking question, custom tour, etc." />
      </div>
      <div>
        <Label htmlFor="c-msg" className="text-[13px]">{t("message")}</Label>
        <Textarea id="c-msg" rows={5} required value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1" />
      </div>
      <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {t("send_message")}
      </button>
    </form>
  );
};

export const ContactInfo = () => {
  const { settings } = useSettings();
  const s = settings || {};
  return (
    <div className="space-y-4 text-[15px] text-[var(--ri-ink-soft)]">
      <div className="flex items-start gap-3">
        <MapPin size={18} className="text-[var(--ri-red)] mt-0.5 shrink-0" />
        <div>
          <div className="font-medium text-[var(--ri-ink)]">Meeting point</div>
          <div><T>{s.meeting_point || "Sultanahmet Square, Istanbul, Türkiye"}</T></div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Mail size={18} className="text-[var(--ri-red)] mt-0.5 shrink-0" />
        <div>
          <div className="font-medium text-[var(--ri-ink)]">Email</div>
          <a href={`mailto:${s.contact_email || "hello@istanbulfreewalkingtours.com"}`} className="link-underline">{s.contact_email || "hello@istanbulfreewalkingtours.com"}</a>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Phone size={18} className="text-[var(--ri-red)] mt-0.5 shrink-0" />
        <div>
          <div className="font-medium text-[var(--ri-ink)]">WhatsApp</div>
          <a href={`tel:${(s.contact_phone || "").replace(/\s/g, "")}`} className="link-underline">{s.contact_phone || "+90 555 000 00 00"}</a>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
